import { useCallback, useEffect, useState } from 'react';
import { formatDate } from '../../util/datetime';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import * as tenantsApi from '../../api/tenants';
import type { Participant, Tenant } from '../../api/tenants';
import { extractErrorMessage } from '../../api/errors';
import { useSelectedTenant } from '../../components/SelectedTenantContext';
import { SortHeader } from '../../components/SortHeader';
import { StartSupportSessionDialog } from '../../components/StartSupportSessionDialog';
import { useTableSort, sortRows } from '../../util/tableSort';
import { SsoConnectionPanel } from './SsoConnectionPanel';
import { TenantLicenseCard } from '../../components/license/TenantLicenseCard';
import { TenantFormDialog } from './TenantFormDialog';

type ParticipantSortKey =
  | 'email'
  | 'full_name'
  | 'email_verified_at'
  | 'is_active'
  | 'joined_at';

/**
 * Superadmin tenant detail page.
 *
 * Reached by clicking a tenant card on the Dashboard or a row in
 * the Tenants list. Shows:
 *   - Tenant summary card (name, slug, billing, status, contact)
 *   - Participant summary (total / active / suspended)
 *   - Participants table
 *
 * The Edit tenant button opens the existing TenantFormDialog in
 * superadmin mode (full edit, no field restrictions) — so this
 * page is the central control surface for a single tenant.
 *
 * Tenant_admin's equivalent is the Dashboard + /tenant/participants
 * + /tenant/settings, which are scoped to their one tenant
 * automatically.
 */
export function TenantDetail() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { setName: setSelectedTenantName } = useSelectedTenant();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [participants, setParticipants] = useState<Participant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Superadmin can't read tenant DATA without a support session; when the
  // participants fetch 403s we swap the table for a "start a session" CTA.
  const [dataBlocked, setDataBlocked] = useState(false);
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  // Active-switch toggling state. Mirrors TenantList's pattern of
  // optimistic UI with revert-on-error so the user gets immediate
  // feedback but is never lied to about persisted state.
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Click-to-sort for the Participants table, matching the
  // tenant_admin list pages (Devices, Procedures, Participants).
  const sort = useTableSort<ParticipantSortKey>('email');

  const reload = useCallback(async () => {
    if (!tenantId) return;
    setError(null);
    try {
      // The tenant itself is management data — a superadmin keeps standing
      // access (SUPPORT-IMPERSONATION), so this must succeed to show the page.
      const t = await tenantsApi.getTenant(tenantId);
      setTenant(t);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load tenant'));
      return;
    }
    // Participants are tenant DATA — a superadmin has no standing access,
    // so a 403 here isn't an error; it means "start a support session to
    // view participants". Load best-effort and degrade to the CTA.
    try {
      const ps = await tenantsApi.listParticipants(tenantId);
      setParticipants(ps);
      setDataBlocked(false);
    } catch {
      setParticipants([]);
      setDataBlocked(true);
    }
  }, [tenantId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Publish the drilled-into tenant's name so the TopBar can render
  // "10NEWTONS — <name>". Clear on unmount so navigating back to a
  // platform-wide page (Dashboard, Tenants list, etc.) drops the
  // suffix.
  useEffect(() => {
    setSelectedTenantName(tenant?.name ?? null);
    return () => setSelectedTenantName(null);
  }, [tenant?.name, setSelectedTenantName]);

  const toggleActive = async () => {
    if (!tenant) return;
    const next = !tenant.is_active;
    setToggling(true);
    setError(null);
    // Optimistic flip — the switch animates immediately. Revert in
    // the catch if the PATCH 4xx's.
    setTenant({ ...tenant, is_active: next });
    try {
      await tenantsApi.updateTenant(tenant.id, { is_active: next });
    } catch (err: unknown) {
      setTenant({ ...tenant, is_active: !next });
      setError(extractErrorMessage(err, 'failed to toggle tenant'));
    } finally {
      setToggling(false);
    }
  };

  const confirmDelete = async () => {
    if (!tenant) return;
    setDeleteSubmitting(true);
    setError(null);
    try {
      await tenantsApi.deleteTenant(tenant.id);
      // The tenant is now soft-deleted; the detail page no longer has
      // a sensible row to show. Bounce back to the list, which
      // already filters out soft-deleted rows by default.
      navigate('/admin/tenants');
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to delete tenant'));
      setDeleting(false);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const backButton = (
    <Button
      component={RouterLink}
      to="/admin/tenants"
      startIcon={<ArrowBackIcon />}
      sx={{ alignSelf: 'flex-start' }}
    >
      Tenants
    </Button>
  );

  // Initial-load failure: no data to render, full-page error.
  if (error && (tenant === null || participants === null)) {
    return (
      <Stack spacing={3}>
        {backButton}
        <Alert severity="error">{error}</Alert>
      </Stack>
    );
  }

  if (tenant === null || participants === null) {
    return (
      <Stack spacing={3}>
        {backButton}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Stack>
    );
  }

  // "Active" = onboarded (signed in) AND not suspended, matching the
  // dashboards + the roster "Invited" status. Pending = added but not yet
  // signed in. Suspended = membership disabled.
  const active = participants.filter(
    (p) => p.is_active && p.has_signed_in,
  ).length;
  const pending = participants.filter(
    (p) => p.is_active && !p.has_signed_in,
  ).length;
  const suspended = participants.filter((p) => !p.is_active).length;
  const verified = participants.filter((p) => p.email_verified_at).length;
  const expValues = participants
    .map((p) => p.years_of_experience)
    .filter((n): n is number => n != null);
  const avgExperience = expValues.length
    ? Math.round(expValues.reduce((a, b) => a + b, 0) / expValues.length)
    : null;
  const v = (s: string | null | undefined) => s ?? '—';

  const sortedParticipants = sortRows(
    participants,
    (p) => {
      switch (sort.orderBy) {
        case 'email':
          return p.email;
        case 'full_name':
          return p.full_name;
        case 'email_verified_at':
          return p.email_verified_at;
        case 'is_active':
          return p.is_active;
        case 'joined_at':
          return p.joined_at;
      }
    },
    sort.order,
  );

  return (
    <Stack spacing={3}>
      {backButton}

      {/* Transient operation error (toggle / delete failure). Sits
          above the header so the user sees what went wrong without
          losing the page they were on. */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
      >
        <Stack>
          <Typography variant="h4">{tenant.name}</Typography>
          {/* Tenant-record header: identity + at-a-glance status/plan/created,
              like a customer account header. (Institution type and last-
              activity would need fields the tenant record doesn't carry yet.) */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.75 }}
          >
            {tenant.is_active ? (
              <Chip label="Active" size="small" color="success" />
            ) : (
              <Chip label="Suspended" size="small" color="warning" />
            )}
            <Chip
              label={tenant.billing_mode.replace('_', ' ')}
              size="small"
              variant="outlined"
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: 'monospace' }}
            >
              {tenant.slug}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              · Created {formatDate(tenant.created_at)}
            </Typography>
          </Stack>
        </Stack>

        {/* Action toolbar — mirrors the row affordances on the
            Tenants list page so the per-tenant capabilities are
            consistent across both entry paths. */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip
            title={
              tenant.is_active
                ? 'Suspend this tenant (members lose access; recoverable).'
                : 'Re-enable this tenant.'
            }
          >
            <FormControlLabel
              control={
                <Switch
                  checked={tenant.is_active}
                  disabled={toggling}
                  onChange={() => void toggleActive()}
                  inputProps={{
                    'aria-label': tenant.is_active
                      ? 'Suspend tenant'
                      : 'Re-enable tenant',
                  }}
                />
              }
              label={tenant.is_active ? 'Active' : 'Suspended'}
            />
          </Tooltip>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<SupportAgentIcon />}
            onClick={() => setSessionDialogOpen(true)}
          >
            Start support session
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => setEditing(true)}
          >
            Edit tenant
          </Button>
          <Tooltip title="Delete tenant (soft-delete — schema preserved).">
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleting(true)}
            >
              Delete
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      {dataBlocked && (
        <Alert severity="info" icon={<SupportAgentIcon fontSize="inherit" />}>
          Participant data isn’t visible to platform admins directly. Start a
          support session to view and work with this tenant’s data.
        </Alert>
      )}

      <StartSupportSessionDialog
        open={sessionDialogOpen}
        onClose={() => setSessionDialogOpen(false)}
        tenantId={tenant.id}
        tenantName={tenant.name}
      />

      <TenantLicenseCard tenantId={tenant.id} />

      {/* Domain stats overview. */}
      <Grid container spacing={2}>
        {/* Counts come from the tenant STATS (management data, standing
            superadmin access), not the participant list — a superadmin
            without an active support session can't read the list (it 403s
            → empty), but must still see the same totals as the Dashboard's
            per-tenant card, which is stats-backed too. */}
        <SummaryCell label="Participants">
          <Typography variant="h5">{tenant.participant_count}</Typography>
          <Typography variant="body2" color="text.secondary">
            <Typography component="span" variant="body2" color="success.main">
              {tenant.active_participant_count} active
            </Typography>
            {' / '}
            <Typography component="span" variant="body2" color="warning.main">
              {tenant.pending_participant_count} pending
            </Typography>
            {' / '}
            <Typography component="span" variant="body2" color="text.secondary">
              {tenant.participant_count -
                tenant.active_participant_count -
                tenant.pending_participant_count}{' '}
              suspended
            </Typography>
          </Typography>
        </SummaryCell>
        {/* Domain stats — match the per-tenant cards on the superadmin
            Dashboard so the operator sees the same numbers wherever
            they land. Devices shows the active-count value (matching
            the per-tenant Dashboard's Devices card) to keep the focus
            on "currently usable" devices. Five tiles at md:2 share one
            row on desktop. */}
        <SummaryCell label="Procedures">
          <Typography variant="h5">{tenant.procedure_count}</Typography>
        </SummaryCell>
        <SummaryCell label="Devices">
          <Typography variant="h5" sx={{ color: 'success.main' }}>
            {tenant.device_active_count}
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              of {tenant.device_count} enrolled
            </Typography>
          </Typography>
        </SummaryCell>
        {/* Edge capacity, same in-service definition the dashboards use:
            appliances that are not retired, and the cameras hanging off
            them. A camera on a retired appliance is not capacity — it
            cannot record, whatever its own status says. */}
        <SummaryCell label="Edge">
          <Typography variant="h5">
            {tenant.appliance_count ?? 0}
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              {(tenant.appliance_count ?? 0) === 1 ? 'appliance' : 'appliances'}
              {', '}
              {tenant.camera_count ?? 0}{' '}
              {(tenant.camera_count ?? 0) === 1 ? 'camera' : 'cameras'}
            </Typography>
          </Typography>
        </SummaryCell>
        <SummaryCell label="Sessions">
          <Typography variant="h5">{tenant.session_count}</Typography>
        </SummaryCell>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Contact
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <FieldCell label="Contact name" value={v(tenant.contact_name)} />
            <FieldCell label="Contact email" value={v(tenant.contact_email)} />
            <FieldCell label="Phone" value={v(tenant.phone)} />
            <FieldCell label="Website" value={v(tenant.website)} />
          </Grid>
        </CardContent>
      </Card>

      {/* Enterprise SSO connection (superadmin-only writes). Owns its own
          load/save lifecycle so a 404 "not configured" doesn't block the
          rest of the page. */}
      <SsoConnectionPanel tenantId={tenant.id} />

      <Stack>
        <Typography variant="h6">Participants</Typography>
        {/* Story strip — the roster at a glance before the table. All
            computed from the participant list. */}
        {participants.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}
          >
            <Chip
              label={`${active} active`}
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
            <Chip
              label={`${pending} pending`}
              size="small"
              color={pending > 0 ? 'warning' : 'default'}
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
            <Chip
              label={`${suspended} suspended`}
              size="small"
              color="default"
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
            <Chip
              label={`${verified} verified`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
            {avgExperience != null && (
              <Chip
                label={`avg ${avgExperience} yrs experience`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
            )}
          </Stack>
        )}
        {participants.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', mt: 1 }}>
            <Typography color="text.secondary">
              This tenant has no participants yet. The tenant admin
              invites them from their Participants page.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <SortHeader label="Email" col="email" sort={sort} />
                  <SortHeader label="Name" col="full_name" sort={sort} />
                  <SortHeader
                    label="Verified"
                    col="email_verified_at"
                    sort={sort}
                  />
                  <SortHeader label="Status" col="is_active" sort={sort} />
                  <SortHeader label="Joined" col="joined_at" sort={sort} />
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedParticipants.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.email}</TableCell>
                    <TableCell>{p.full_name ?? '—'}</TableCell>
                    <TableCell>
                      {p.email_verified_at ? (
                        <Chip label="Verified" size="small" color="success" />
                      ) : (
                        <Chip label="Pending" size="small" color="warning" />
                      )}
                    </TableCell>
                    <TableCell>
                      {p.is_active ? (
                        <Chip label="Active" size="small" color="success" />
                      ) : (
                        <Chip label="Suspended" size="small" color="warning" />
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(p.joined_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      {editing && (
        <TenantFormDialog
          mode="edit"
          tenant={tenant}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void reload();
          }}
        />
      )}

      <Dialog open={deleting} onClose={() => setDeleting(false)}>
        <DialogTitle>Delete {tenant.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This soft-deletes the tenant. The row remains in the
            database so the slug <code>{tenant.slug}</code> stays
            reserved and the per-tenant Postgres schema is preserved
            — but the tenant is removed from active listings and
            members can no longer access it.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }} color="text.secondary">
            If you need a true hard-delete (drop the schema + row),
            open an ops ticket; that's intentionally not exposed in
            the UI.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleting(false)}
            disabled={deleteSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void confirmDelete()}
            color="error"
            variant="contained"
            disabled={deleteSubmitting}
          >
            {deleteSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ---------------------------------------------------------------

function SummaryCell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  // height: '100%' lets the Paper stretch to the tallest sibling in
  // the same Grid row so every summary tile shares a baseline.
  // md: 2 of 12, so up to six fit one row; 3-across on tablet, 2-up on phone.
  return (
    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
      <Paper sx={{ p: 2, height: '100%' }}>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Box sx={{ mt: 1 }}>{children}</Box>
      </Paper>
    </Grid>
  );
}

function FieldCell({ label, value }: { label: string; value: string }) {
  return (
    <Grid size={{ xs: 12, sm: 6 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography>{value}</Typography>
    </Grid>
  );
}
